/**
 * LoginPage — sign-in screen + inline "Forgot password" sub-flow.
 *
 * Refactored to use the shared `AuthShell` / `AuthInput` / `AuthSubmit`
 * components so it stays visually identical to Register and Reset.
 *
 * Behavioral contract (UNCHANGED — do not modify without auth review):
 *   • Calls `useAuth().login(email, password)` which posts to /api/auth/login
 *     and stores the session cookie. Loaded user state propagates via
 *     AuthContext, which redirects on success.
 *   • Forgot-password POSTs to /api/auth/forgot-password and either
 *     emails a link OR returns `reset_token` for in-app debug flows
 *     (test envs without SMTP) — we render the link button only when
 *     the token is present so production users don't see the dev path.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import AuthShell, { AuthHeader, AuthAlert } from '@/components/auth/AuthShell';
import AuthInput from '@/components/auth/AuthInput';
import AuthSubmit from '@/components/auth/AuthSubmit';
import { takeLoginNotice } from '@/lib/loginNotice';
import { Button } from '@/components/ui/button';
import { Turnstile } from '@marsidev/react-turnstile';

// Basic RFC-5322-ish email check — good enough for client-side guard;
// authoritative validation lives on the backend.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Map the wide variety of axios failure shapes to a friendly,
 * non-technical message. Never leak raw stack traces or backend
 * internals to the user.
 */
function describeLoginError(err) {
  // Network failure / CORS / server unreachable — axios sets no `response`.
  if (!err.response) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  const { status, data } = err.response;
  const detail = formatApiErrorDetail(data?.detail);
  if (status === 401) return 'Invalid email or password.';
  if (status === 429) return detail || 'Too many failed attempts. Try again in 15 minutes.';
  if (status === 422) return 'Please enter a valid email and password.';
  if (status >= 500) return 'Our servers ran into a problem. Please try again in a moment.';
  return detail || 'Sign-in failed. Please try again.';
}

export default function LoginPage() {
  const { login, verifyMfa } = useAuth();

  // ─── Sign-in form state ───
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice] = useState(takeLoginNotice);  // why the last session was signed out (once)
  const [loading, setLoading] = useState(false);
  // Ref-based lock prevents the rare double-submit when a user
  // mashes Enter before React has committed `loading=true`.
  const submitLockRef = useRef(false);

  // ─── 2FA challenge sub-view state ───
  const [mfaChallenge, setMfaChallenge] = useState('');
  const [mfaEmailHint, setMfaEmailHint] = useState('');
  const [mfaDevCode, setMfaDevCode] = useState('');
  const [mfaMethod, setMfaMethod] = useState('email');
  const [mfaHint, setMfaHint] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resendMsg, setResendMsg] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  // ─── Forgot-password sub-view state ───
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const doLogin = async (tokenOverride) => {
    setError('');
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password;
    if (!cleanEmail || !cleanPassword) {
      setError('Please enter your email and password.');
      return;
    }
    if (!EMAIL_REGEX.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    submitLockRef.current = true;
    setLoading(true);
    try {
      const res = await login(cleanEmail, cleanPassword, tokenOverride ?? turnstileToken);
      // Bot challenge required → reveal the Turnstile widget and wait for a token.
      if (res && res.captcha_required) {
        setCaptchaRequired(true);
        setTurnstileToken(null);
        setError(res.message || 'Please complete the verification below.');
        return;
      }
      // 2FA required → switch to the email-code entry step.
      if (res && res.mfa_required) {
        setMfaChallenge(res.challenge_token);
        setMfaEmailHint(res.email_hint || '');
        setMfaMethod(res.method || 'email');
        setMfaHint(res.hint || res.email_hint || '');
        setMfaDevCode(res.dev_code || '');
      }
      // Otherwise AuthContext set `user`; <PublicRoute> redirects.
    } catch (err) {
      setError(describeLoginError(err));
      setTurnstileToken(null);
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitLockRef.current) return;       // double-submit guard
    await doLogin();
  };

  // Once the user solves the challenge, resubmit automatically with the token.
  useEffect(() => {
    if (captchaRequired && turnstileToken && !submitLockRef.current) {
      doLogin(turnstileToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnstileToken]);

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMsg('');
    const code = (mfaCode || '').trim();
    if (!code) { setError('Enter the 6-digit code we emailed you.'); return; }
    setMfaLoading(true);
    try {
      await verifyMfa(mfaChallenge, code, rememberDevice);
      // success → AuthContext sets user → redirect
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // challenge expired — send them back to password step
        setMfaChallenge('');
        setError('Your verification session expired. Please log in again.');
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || 'Invalid code.');
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResendMsg('');
    setResendLoading(true);
    try {
      const { data } = await apiClient.post('/mfa/resend-code', { challenge_token: mfaChallenge });
      setResendMsg('A new code is on its way to your email.');
      if (data && data.dev_code) setMfaDevCode(data.dev_code);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not resend the code just yet.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    try {
      const { data } = await apiClient.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
      // Test envs return the token directly (no email backend wired up).
      if (data.reset_token) setResetToken(data.reset_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to send reset link');
    } finally {
      setForgotLoading(false);
    }
  };

  const switchToForgot = () => {
    setShowForgot(true);
    // Pre-fill the forgot-email field with whatever the user already
    // typed in the sign-in form so they don't have to retype it.
    setForgotEmail(email);
    setError('');
  };

  return (
    <AuthShell testId="login-page">
      {mfaChallenge ? (
        <TwoFactorChallengeView
          code={mfaCode}
          setCode={setMfaCode}
          emailHint={mfaEmailHint}
          method={mfaMethod}
          hint={mfaHint}
          remember={rememberDevice}
          setRemember={setRememberDevice}
          loading={mfaLoading}
          error={error}
          resendMsg={resendMsg}
          resendLoading={resendLoading}
          onResend={handleResendCode}
          onSubmit={handleMfaSubmit}
          devCode={mfaDevCode}
          onBack={() => { setMfaChallenge(''); setMfaCode(''); setError(''); setResendMsg(''); setMfaDevCode(''); }}
        />
      ) : showForgot ? (
        <ForgotPasswordView
          email={forgotEmail}
          setEmail={setForgotEmail}
          loading={forgotLoading}
          sent={forgotSent}
          resetToken={resetToken}
          error={error}
          onSubmit={handleForgotPassword}
          onBack={() => { setShowForgot(false); setForgotSent(false); setError(''); }}
        />
      ) : (
        <SignInView
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          loading={loading}
          error={error}
          notice={notice}
          onSubmit={handleSubmit}
          onForgot={switchToForgot}
          captchaRequired={captchaRequired}
          siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
          onCaptchaToken={setTurnstileToken}
        />
      )}
    </AuthShell>
  );
}

function TwoFactorChallengeView({ code, setCode, emailHint, method, hint, remember, setRemember, loading, error, resendMsg, resendLoading, onResend, onSubmit, devCode, onBack }) {
  const isSms = method === 'sms';
  const target = hint || emailHint;
  return (
    <>
      <AuthHeader
        title={isSms ? 'Check your phone' : 'Check your email'}
        subtitle={target
          ? `We sent a 6-digit verification code ${isSms ? 'by text to' : 'to'} ${target}. Enter it below to finish signing in.`
          : (isSms
              ? 'We texted you a 6-digit verification code. Enter it below to finish signing in.'
              : 'We emailed you a 6-digit verification code. Enter it below to finish signing in.')}
      />
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        {error && <AuthAlert kind="error" testId="mfa-error">{error}</AuthAlert>}
        {resendMsg && <AuthAlert kind="success" testId="mfa-resend-msg">{resendMsg}</AuthAlert>}
        {devCode && (
          <div
            data-testid="mfa-dev-code"
            className="text-xs rounded-md px-3 py-2"
            style={{ background: 'rgba(69,117,168,0.12)', color: '#8fb4dd', border: '1px solid rgba(69,117,168,0.25)' }}
          >
            Dev/preview mode (email delivery not configured): your code is <strong>{devCode}</strong>
          </div>
        )}
        <AuthInput
          id="mfa-code"
          label="Verification code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          autoFocus
          autoComplete="one-time-code"
          inputMode="numeric"
          placeholder="123456"
          testId="mfa-code-input"
        />
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: '#A1A1AA' }} data-testid="mfa-remember-label">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            data-testid="mfa-remember-checkbox"
            className="w-4 h-4 rounded accent-[#4575A8]"
          />
          Remember this device for 30 days
        </label>
        <AuthSubmit loading={loading} loadingLabel="Verifying…" testId="mfa-verify-button">
          Verify
        </AuthSubmit>
        <button
          type="button"
          onClick={onResend}
          disabled={resendLoading}
          data-testid="mfa-resend-button"
          className="auth-link text-xs mx-auto block disabled:opacity-50"
          style={{ color: '#4575A8' }}
        >
          {resendLoading ? 'Sending…' : "Didn't get it? Resend code"}
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="mfa-back-button"
          className="auth-link text-xs flex items-center gap-1 mx-auto"
          style={{ color: '#71717A' }}
        >
          <ArrowLeft className="w-3 h-3" /> Back to login
        </button>
      </form>
    </>
  );
}

function SignInView({ email, setEmail, password, setPassword, loading, error, notice, onSubmit, onForgot, captchaRequired, siteKey, onCaptchaToken }) {
  return (
    <>
      <AuthHeader
        title="Welcome Back"
        subtitle="Log in to access your project management workspace."
      />
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        {notice && !error && <AuthAlert kind="info" testId="login-notice">{notice}</AuthAlert>}
        {error && <AuthAlert kind="error" testId="login-error">{error}</AuthAlert>}

        <AuthInput
          id="login-email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          placeholder="you@company.com"
          testId="login-email-input"
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-[11px] font-medium tracking-[0.15em] uppercase"
              style={{ color: '#71717A' }}
            >
              Password
            </span>
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={onForgot}
              className="auth-link text-xs"
              style={{ color: '#4575A8' }}
            >
              Forgot password?
            </button>
          </div>
          <AuthInput
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="Enter your password"
            withVisibilityToggle
            testId="login-password-input"
          />
        </div>

        {captchaRequired && siteKey && (
          <div className="flex flex-col items-center gap-2 pt-1" data-testid="turnstile-widget">
            <span className="text-xs" style={{ color: '#A1A1AA' }}>
              Please complete the security check to continue.
            </span>
            <Turnstile
              siteKey={siteKey}
              onSuccess={onCaptchaToken}
              onError={() => onCaptchaToken(null)}
              onExpire={() => onCaptchaToken(null)}
              options={{ theme: 'dark' }}
            />
          </div>
        )}

        <AuthSubmit
          loading={loading}
          loadingLabel="Logging in…"
          testId="login-submit-button"
        >
          Log In
        </AuthSubmit>
        <p className="text-center text-sm mt-2" style={{ color: 'var(--strata-text-muted, #8A8F99)' }} data-testid="login-invite-help">
          New here? Ask your company admin for an invite.
        </p>
      </form>
    </>
  );
}

function ForgotPasswordView({ email, setEmail, loading, sent, resetToken, error, onSubmit, onBack }) {
  return (
    <>
      <button
        onClick={onBack}
        data-testid="back-to-login"
        className="auth-link flex items-center gap-1.5 text-xs mb-4"
        style={{ color: '#71717A' }}
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
      </button>
      <AuthHeader
        title="Reset Password"
        subtitle={
          sent
            ? 'A reset link has been generated.'
            : "Enter your email and we'll send you reset instructions."
        }
      />

      {sent ? (
        <div className="space-y-4">
          <AuthAlert kind="success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium" style={{ color: '#F2F0EB' }}>Check your email</p>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1A5' }}>
                  If an account exists for that address, you'll receive reset instructions shortly.
                </p>
              </div>
            </div>
          </AuthAlert>
          {/* Inline "open reset" button — only renders in test envs that
              return the token directly (no SMTP wired). */}
          {resetToken && (
            <Link to={`/reset-password?token=${resetToken}`} data-testid="reset-password-link">
              <Button className="auth-btn w-full h-11 text-sm font-medium rounded-lg bg-[#4575A8] hover:bg-[#5C8AC2] text-white">
                Open Reset Link
              </Button>
            </Link>
          )}
          <button
            onClick={onBack}
            className="w-full text-center text-xs auth-link"
            style={{ color: '#71717A' }}
          >
            Back to Sign In
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5">
          {error && <AuthAlert kind="error">{error}</AuthAlert>}
          <AuthInput
            id="forgot-email"
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@company.com"
            testId="forgot-email-input"
          />
          <AuthSubmit
            loading={loading}
            loadingLabel="Sending…"
            testId="forgot-submit-button"
          >
            Send Reset Link
          </AuthSubmit>
        </form>
      )}
    </>
  );
}
